import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Bell, CheckCircle, Loader2, MailOpen, RotateCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatAnnouncementDate = (value) => {
  if (!value) return "Unknown date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const AnnouncementsPanel = ({ token, onUnreadCountChange, testId = "announcements-panel" }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [markingReadId, setMarkingReadId] = useState("");

  const updateUnreadCount = useCallback((count) => {
    setUnreadCount(count);
    onUnreadCountChange?.(count);
  }, [onUnreadCountChange]);

  const fetchAnnouncements = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/announcements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnnouncements(response.data.announcements || []);
      updateUnreadCount(response.data.unread_count || 0);
      setSelectedId((current) => current || response.data.announcements?.[0]?.id || "");
    } catch (error) {
      setAnnouncements([]);
      updateUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [token, updateUnreadCount]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const markRead = async (announcement) => {
    setSelectedId(announcement.id);
    if (announcement.read || markingReadId) return;
    setMarkingReadId(announcement.id);
    try {
      await axios.post(`${API}/announcements/${announcement.id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnnouncements((current) => {
        const next = current.map((item) => (
          item.id === announcement.id ? { ...item, read: true } : item
        ));
        updateUnreadCount(next.filter((item) => !item.read).length);
        return next;
      });
    } finally {
      setMarkingReadId("");
    }
  };

  const markUnread = async (announcement) => {
    if (!announcement?.id || !announcement.read || markingReadId) return;
    setMarkingReadId(announcement.id);
    try {
      await axios.post(`${API}/announcements/${announcement.id}/unread`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnnouncements((current) => {
        const next = current.map((item) => (
          item.id === announcement.id ? { ...item, read: false } : item
        ));
        updateUnreadCount(next.filter((item) => !item.read).length);
        return next;
      });
    } finally {
      setMarkingReadId("");
    }
  };

  const selectedAnnouncement = announcements.find((item) => item.id === selectedId) || announcements[0];

  return (
    <Card className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Bell className="w-5 h-5 text-[#42D78A]" /> Announcements
        </CardTitle>
        <Badge className={unreadCount ? "bg-[#42D78A] text-[#1a1a1a]" : "bg-slate-100 text-slate-500"}>
          {unreadCount} unread
        </Badge>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center rounded-xl bg-slate-50 p-8 text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading announcements
          </div>
        ) : announcements.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
            No announcements yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(16rem,0.8fr)_minmax(0,1.2fr)]">
            <div className="space-y-2">
              {announcements.map((announcement) => (
                <button
                  key={announcement.id}
                  type="button"
                  onClick={() => markRead(announcement)}
                  className={`w-full rounded-xl border p-4 text-left transition-all ${selectedAnnouncement?.id === announcement.id ? "border-[#42D78A] bg-[#42D78A]/10" : "border-slate-100 bg-slate-50 hover:bg-slate-100"}`}
                  data-testid={`announcement-${announcement.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {announcement.subject}
                      </p>
                      <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                        {announcement.sender_name}
                      </p>
                    </div>
                    {!announcement.read ? (
                      <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-[#42D78A]" aria-label="Unread" />
                    ) : (
                      <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
                    )}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">{formatAnnouncementDate(announcement.created_at)}</p>
                  {announcement.preview && <p className="mt-2 line-clamp-2 text-sm text-slate-600">{announcement.preview}</p>}
                </button>
              ))}
            </div>
            <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Message</p>
                  <h3 className="mt-1 text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {selectedAnnouncement?.subject}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    {selectedAnnouncement?.sender_name} · {formatAnnouncementDate(selectedAnnouncement?.created_at)}
                  </p>
                </div>
                {selectedAnnouncement?.read ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => markUnread(selectedAnnouncement)}
                    disabled={markingReadId === selectedAnnouncement.id}
                    className="rounded-full border-slate-200 text-slate-600 hover:bg-slate-50"
                  >
                    {markingReadId === selectedAnnouncement.id ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="mr-2 h-4 w-4" />
                    )}
                    Mark unread
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => markRead(selectedAnnouncement)}
                    disabled={markingReadId === selectedAnnouncement?.id}
                    className="rounded-full bg-[#42D78A] text-[#1a1a1a] hover:bg-[#3bc27c]"
                  >
                    {markingReadId === selectedAnnouncement?.id ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <MailOpen className="mr-2 h-4 w-4" />
                    )}
                    Mark read
                  </Button>
                )}
              </div>
              <div
                className="prose prose-sm max-w-none text-slate-700 [&_a]:font-bold [&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-2 [&_img]:max-w-full [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
                dangerouslySetInnerHTML={{ __html: selectedAnnouncement?.message || "<p>No message content.</p>" }}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AnnouncementsPanel;
